import { useState, useMemo, useEffect } from "react"
import { getUserGeography } from "routes/sfg20-admin/plugins/system-info/location-analytics-tab/controller/get-user-geography"
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    Grid2,
} from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { DATE_FORMATS } from "lib/@constants/date-format"
import { DownloadButton } from "routes/sfg20-admin/plugins/system-info/common/components/download-button"
import { GenericPagination } from "lib/@components/paging/generic-pagination"
import dayjs from "dayjs"

export function Geography() {
    const data = getUserGeography.useResults()
    const geographicSessionData = data?.results

    const [filters, setFilters] = useState({
        client: "",
        user: "",
        country: "",
        ip: "",
        startDate: null,
        endDate: null,
    })

    const [currentPage, setCurrentPage] = useState(1)
    const [itemsPerPage, setItemsPerPage] = useState(10)

    const geoArray = useMemo(() => {
        if (!geographicSessionData) return []
        const arr = []
        geographicSessionData.forEach((geoHistory) => {
            Object.keys(geoHistory.geoHistory).forEach((countryCode) => {
                Object.keys(geoHistory.geoHistory[countryCode]).forEach((city) => {
                    Object.keys(geoHistory.geoHistory[countryCode][city]).forEach((ip) => {
                        const ipData = geoHistory.geoHistory[countryCode][city][ip]
                        arr.push({
                            client: geoHistory.clientName,
                            user: geoHistory.UserName,
                            country: countryCode,
                            city,
                            ip,
                            firstSeen: new Date(ipData.firstSeen),
                            lastSeen: new Date(ipData.lastSeen),
                        })
                    })
                })
            })
        })
        return arr
    }, [geographicSessionData])

    const filteredGeoArray = useMemo(
        () =>
            geoArray.filter((item) => {
                const matchesText = (value, filter) => !filter || value.toLowerCase().includes(filter.toLowerCase())

                const matchesDateRange = () => {
                    if (!filters.startDate && !filters.endDate) return true
                    const firstSeen = dayjs(item.firstSeen)
                    const start = filters.startDate ? dayjs(filters.startDate) : null
                    const end = filters.endDate ? dayjs(filters.endDate) : null
                    return (
                        (!start || firstSeen.isAfter(start.subtract(1, "day"))) &&
                        (!end || firstSeen.isBefore(end.add(1, "day")))
                    )
                }

                return (
                    matchesText(item.client, filters.client) &&
                    matchesText(item.user, filters.user) &&
                    matchesText(item.country, filters.country) &&
                    matchesText(item.ip, filters.ip) &&
                    matchesDateRange()
                )
            }),
        [geoArray, filters]
    )

    // Reset to first page when filters change
    useEffect(() => {
        setCurrentPage(1)
    }, [filters])

    const paginatedGeoArray = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage
        const endIndex = startIndex + itemsPerPage
        return filteredGeoArray.slice(startIndex, endIndex)
    }, [filteredGeoArray, currentPage, itemsPerPage])

    const numberOfPages = useMemo(
        () => Math.max(1, Math.ceil(filteredGeoArray.length / itemsPerPage)),
        [filteredGeoArray, itemsPerPage]
    )

    if (!geographicSessionData) return null

    return (
        <Box sx={{ padding: 4 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Grid2 container spacing={2} sx={{ marginBottom: 2 }}>
                    <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
                        <TextField
                            label="Client"
                            value={filters.client}
                            fullWidth
                            onChange={(e) => setFilters((f) => ({ ...f, client: e.target.value }))}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
                        <TextField
                            label="User"
                            value={filters.user}
                            fullWidth
                            onChange={(e) => setFilters((f) => ({ ...f, user: e.target.value }))}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
                        <TextField
                            label="Country"
                            value={filters.country}
                            fullWidth
                            onChange={(e) => setFilters((f) => ({ ...f, country: e.target.value }))}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
                        <TextField
                            label="IP"
                            value={filters.ip}
                            fullWidth
                            onChange={(e) => setFilters((f) => ({ ...f, ip: e.target.value }))}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
                        <DatePicker
                            label="Start Date"
                            value={filters.startDate}
                            onChange={(val) => setFilters((f) => ({ ...f, startDate: val }))}
                            renderInput={(params) => <TextField fullWidth {...params} />}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
                        <DatePicker
                            label="End Date"
                            value={filters.endDate}
                            onChange={(val) => setFilters((f) => ({ ...f, endDate: val }))}
                            renderInput={(params) => <TextField fullWidth {...params} />}
                        />
                    </Grid2>
                </Grid2>
            </LocalizationProvider>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Client</TableCell>
                            <TableCell>User</TableCell>
                            <TableCell>Country</TableCell>
                            <TableCell>City</TableCell>
                            <TableCell>IP</TableCell>
                            <TableCell>First Seen</TableCell>
                            <TableCell>Last Seen</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedGeoArray.map((row, i) => (
                            <TableRow key={`${row.ip}-${i}`}>
                                <TableCell>{row.client}</TableCell>
                                <TableCell>{row.user}</TableCell>
                                <TableCell>{row.country}</TableCell>
                                <TableCell>{row.city}</TableCell>
                                <TableCell>{row.ip}</TableCell>
                                <TableCell>{row.firstSeen.format(DATE_FORMATS.long)}</TableCell>
                                <TableCell>{row.lastSeen.format(DATE_FORMATS.long)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box mt={2} mb={2}>
                <GenericPagination
                    numberOfPages={numberOfPages}
                    currentPage={currentPage}
                    changePage={(page) => setCurrentPage(page)}
                    take={itemsPerPage}
                    changeRowsPerPage={(value) => {
                        setItemsPerPage(value)
                        setCurrentPage(1) // Reset to first page when changing items per page
                    }}
                />
            </Box>

            <Box mt={2} display="flex" justifyContent="flex-end">
                {/* Download button uses all filtered data, not just the current page */}
                <DownloadButton
                    data={{ value: filteredGeoArray }}
                    columns={[
                        { title: "Client", value: "client" },
                        { title: "User", value: "user" },
                        { title: "Country", value: "country" },
                        { title: "City", value: "city" },
                        { title: "IP", value: "ip" },
                        { title: "First Seen", value: "firstSeen" },
                        { title: "Last Seen", value: "lastSeen" },
                    ]}
                    title="Geo Data"
                />
            </Box>
        </Box>
    )
}
